import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ViewListIcon from '@mui/icons-material/ViewList';
import type { OpenerGroup } from '../../api/useGroups';
import {
  OpenerGroupPodState,
  useOpenerGroupsPodState
} from '../../api/useOpenerGroupPods';
import {
  GROUP_POD_STATUS_DOT_COLORS,
  GROUP_POD_STATUS_LABELS,
  deriveGroupPodStatus
} from './groupPodStatus';
import { groupSchedule } from './groupSchedule';

/**
 * Group-picker landing for the opener page's `'all'` selection. The default
 * view no longer loads the rules table (which would fire the unfiltered
 * ~9.5k-doc query that starves the core pod, see `useEntryRules`); instead the
 * operator picks a group to open it group-scoped. Rendered ONLY for `'all'`,
 * and only where the rules table is NOT — the table must never mount with the
 * query disabled.
 *
 * Reuses existing sources only: `groups` is the already-sorted list from
 * `useOpenerGroups` (caller sorts with the shared `sortGroups`), pod status is
 * derived from `useOpenerGroupsPodState` via the shared `deriveGroupPodStatus`.
 * Start/stop lives in `GroupPodControls` once a group is selected — here a
 * status dot is enough.
 */
/**
 * How often the rows re-read the clock. Matches the group list's own
 * `refetchInterval`, so a row never carries a label older than the payload it
 * came from. Exported so its test advances by the real period instead of
 * hard-coding a number that a change here would silently invalidate.
 */
export const SCHEDULE_TICK_MS = 30_000;

export const GroupPickerLanding = ({
  groups,
  onSelectGroup,
  onSelectUngrouped
}: {
  /** Already sorted by the caller with the shared `sortGroups`. */
  groups: OpenerGroup[];
  onSelectGroup: (groupId: string) => void;
  onSelectUngrouped: () => void;
}) => {
  // Ticking clock (30s), NOT a bare `Date.now()` per render: react-query gates
  // `refetchInterval` on window focus, so a picker left open on a second
  // monitor stops refetching and stops re-rendering — and a `now` frozen with
  // it keeps announcing a start time the clock has already passed.
  //
  // It buys correctness, not freshness: the counts still come from the last
  // payload, so an event starting on an unfocused window drops its label
  // rather than turning into a LIVE chip.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), SCHEDULE_TICK_MS);
    return () => clearInterval(id);
  }, []);

  const { data: podStates } = useOpenerGroupsPodState();
  const stateByGroup = useMemo(() => {
    const map = new Map<string, OpenerGroupPodState>();
    for (const s of podStates ?? []) map.set(s.groupId, s);
    return map;
  }, [podStates]);

  return (
    <Box sx={{ overflowY: 'auto', py: 2, px: 1 }}>
      <Typography sx={{ color: 'grey.400', fontSize: '0.85rem', mb: 2 }}>
        Pick a group to open it. The full rule list is only loaded per group —
        the opener page no longer loads every rule at once.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ViewListIcon />}
          component={RouterLink}
          to="/predictions/opening/bucket"
          sx={{ fontSize: '0.75rem' }}
        >
          Pairs Bucket
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={onSelectUngrouped}
          sx={{ fontSize: '0.75rem' }}
        >
          Ungrouped
        </Button>
      </Stack>

      {groups.length === 0 ? (
        <Typography sx={{ color: 'grey.500', fontSize: '0.85rem' }}>
          No groups yet. Create one from the Pairs Bucket page.
        </Typography>
      ) : (
        <Stack spacing={0.75} sx={{ maxWidth: 520 }}>
          {groups.map(g => {
            const status = deriveGroupPodStatus(stateByGroup.get(g._id));
            const { liveCount, nextStartLabel } = groupSchedule(g, nowMs);
            return (
              <Button
                key={g._id}
                variant="outlined"
                onClick={() => onSelectGroup(g._id)}
                sx={{
                  justifyContent: 'space-between',
                  textTransform: 'none',
                  color: 'common.white',
                  borderColor: 'grey.800',
                  px: 1.5,
                  py: 1
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ minWidth: 0 }}
                >
                  <FiberManualRecordIcon
                    sx={{
                      fontSize: '0.7rem',
                      flexShrink: 0,
                      color: GROUP_POD_STATUS_DOT_COLORS[status]
                    }}
                  />
                  <Typography noWrap sx={{ fontSize: '0.85rem' }}>
                    {g.name}
                  </Typography>
                  {g.dynamic && (
                    <Chip
                      label="auto"
                      size="small"
                      sx={{ height: 16, fontSize: '0.6rem', flexShrink: 0 }}
                    />
                  )}
                  {liveCount > 0 && (
                    // Same pill the rule rows use for a started event
                    // (`EntryTabByPath`) — one LIVE look across the app.
                    <Box
                      sx={{
                        px: 0.4,
                        flexShrink: 0,
                        borderRadius: '3px',
                        backgroundColor: '#2e7d32',
                        color: '#fff',
                        fontSize: '0.55rem',
                        fontWeight: 700,
                        lineHeight: 1.4,
                        letterSpacing: '0.03em',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      LIVE {liveCount}
                    </Box>
                  )}
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ flexShrink: 0 }}
                >
                  {nextStartLabel && (
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        color: 'grey.500',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {nextStartLabel}
                    </Typography>
                  )}
                  <Typography
                    sx={{ fontSize: '0.7rem', color: 'grey.500' }}
                  >
                    {GROUP_POD_STATUS_LABELS[status]}
                  </Typography>
                  <Typography
                    sx={{ fontSize: '0.7rem', color: 'grey.500' }}
                  >
                    {g.pairCount} pair{g.pairCount === 1 ? '' : 's'}
                  </Typography>
                </Stack>
              </Button>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};
